import { Spin } from "antd";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AppBootstrap } from "@xemplo/app-bootstrap";
import { AuthProvider } from "@xemplo/auth-provider";
import { FeatureProvider } from "@xemplo/feature-flag";
import { RootStyle } from "@xemplo/root-style";

import App from "./App";
import { featureMap } from "./constants";
import { AUTH_CONFIG, DS_SESSION_ID_STORAGE_KEY } from "./constants/auth";
import environment from "./constants/environment";
import { handleReturnUrl } from "./libs/return-url.helper";
import * as serviceWorker from "./serviceWorker";
import store, { persistor } from "./store";
import { SupportedApps, XemploAppSettings } from "@xemplo/types";
import { memberActions } from "@xemplo/gp-member-store";

const script_chart = document.createElement(
  "script_chart",
) as HTMLScriptElement;
script_chart.src = "https://www.gstatic.com/charts/loader.js";
script_chart.async = true;
document.body.appendChild(script_chart);

// tinymce
const script_tinymce = document.createElement(
  "script_tinymce",
) as HTMLScriptElement;
script_tinymce.src =
  "https://cdn.tiny.cloud/1/zozyl0xsr52trib3dncl1q2jbaii3ww2bc90pv29suh366v7/tinymce/5/tinymce.min.js";
script_tinymce.async = true;
document.body.appendChild(script_tinymce);

const appSettingsConfig = {
  APP_CLIENT_ID: "expedo.globaldash",
  VITE_APP_NAME: SupportedApps.GP,
  VITE_CLOUD_ROLE: `gp-${environment.instance}`,
  VITE_DS_URL: environment.directory_service_url,
  VITE_EIMS_URL: environment.identity_baseurl,
  VITE_INSTRUMENTATION_KEY: environment.insight_key,
  VITE_RELEASE_CHANNEL: environment.instance,
  VITE_SENTRY_DSN: "",
  VITE_SIGNALR_URL: environment.signalRUrl,
  VITE_START_APP_URL: environment.start_app_url,
  applicationApiUrl: environment.baseurl,
  useInstanceUrlOnPersonaSwitch: false,

  // The @xemplo/auth-helper Logout clears its own concerns (session state,
  // cookie, query cache, persona) but has no knowledge of the GP redux member
  // store. Wire the member reset in via the app-settings logout callback so the
  // shared Logout still tears down member state on sign-out. Also drop the
  // localStorage mirror of dsSessionId (see DsSessionIdSync in App.tsx) so a
  // later sign-in doesn't inherit a stale session id.
  logoutCallback: () => {
    store.dispatch(memberActions.reset());
    localStorage.removeItem(DS_SESSION_ID_STORAGE_KEY);
  },
};

// This is needed to handle refreshes on the page whilst the router is not
// completely fixed. The current routing implementation is not retaining the url from the browser
// and combined with the login redirects it causes some funky behaviours.
handleReturnUrl();
const client = new QueryClient();
const rootElement = createRoot(document.getElementById("root") as HTMLElement);
rootElement.render(
  // `AppBootstrap` provides StrictMode, App Insights, the Xemplo app-settings
  // context, cross-app/mobile token handover and the session-state gate.
  // Its `settings` prop is typed as the full `XemploAppSettings` (including the
  // internal state/setters the provider synthesizes at runtime and which the
  // type comments say NOT to set here), so we cast the base config at the
  // boundary. TODO: tighten `AppBootstrap`'s prop type upstream to the base
  // app-settings shape so this cast can be removed.
  <AppBootstrap settings={appSettingsConfig as unknown as XemploAppSettings}>
    <FeatureProvider
      featureMap={featureMap}
      environment={environment.instance}
      appContext="gp"
    >
      <QueryClientProvider client={client}>
        <Provider store={store}>
          <PersistGate loading={<Spin />} persistor={persistor}>
            <AuthProvider {...AUTH_CONFIG}>
              <RootStyle />
              <App />
            </AuthProvider>
          </PersistGate>
        </Provider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </FeatureProvider>
  </AppBootstrap>,
);
serviceWorker.unregister();
